import { useEffect, useState } from 'react';
import { HelpLink } from './DirectorHelp.tsx';
import {
  MAX_RESULTS_INTERVAL,
  MIN_RESULTS_INTERVAL,
  RESULTS_FIELDS,
  RESULTS_SOURCES,
  emptyFieldMap,
  formatClock,
  parseResultsAddress,
  raceStatusLabel,
  resultsPhaseLabel,
  type ResultsConfigInput,
  type ResultsFieldMap,
  type ResultsProbe,
  type ResultsSource,
  type ResultsStatus,
} from '@rabbitfuel/shared';
import { api } from '../lib/api.ts';
import { timeShort } from '../lib/format.ts';

/**
 * Woher die Zieleinlaeufe kommen: aus der Regie oder aus RACE RESULT.
 *
 * Das Panel stellt nur ein -- abgefragt wird auf dem Server. Deshalb steht
 * hier nach einem Reload sofort wieder „holt Ergebnisse, zuletzt 14:22", und
 * deshalb geht der API-Key per REST hoch statt ueber die Event-WebSocket.
 *
 * Der Testknopf ist kein Beiwerk, sondern der Kern: welche Spalten eine
 * Ergebnisliste hat, entscheidet der Veranstalter in seinem RACE RESULT.
 * Die Testabfrage zeigt die Rohantwort neben unserer Lesart -- passt die
 * Zuordnung nicht, wird sie unten von Hand gestellt, ohne Code.
 */
export function ResultsPanel({
  slug,
  pin,
  status,
}: {
  slug: string;
  pin: string | undefined;
  status: ResultsStatus;
}) {
  const [source, setSource] = useState<ResultsSource>(status.config.source);
  const [eventKey, setEventKey] = useState(status.config.eventKey);
  const [apiKey, setApiKey] = useState('');
  const [interval, setIntervalSeconds] = useState(String(status.config.intervalSeconds));
  const [imports, setImports] = useState(status.config.imports);
  const [fields, setFields] = useState<ResultsFieldMap>({ ...emptyFieldMap(), ...status.config.fields });
  const [showFields, setShowFields] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [probe, setProbe] = useState<ResultsProbe | null>(null);

  // Ob der Server die Anbindung ueberhaupt anbietet, weiss nur der Server.
  useEffect(() => {
    api
      .getResults(slug, pin)
      .then((r) => setEnabled(r.enabled))
      .catch(() => undefined);
  }, [slug, pin]);

  /*
   * Den Stand vom Server nachziehen -- beim ersten `welcome` genauso wie
   * nach einer Aenderung durch eine zweite Regie.
   *
   * Die Abhaengigkeiten sind bewusst die *Werte* und nicht `status.config`:
   * das Objekt ist bei jedem Takt der Abfrage ein neues, und ein Effekt
   * darauf wuerde alle zehn Sekunden mitten ins Tippen greifen. So laeuft er
   * nur, wenn sich wirklich etwas geaendert hat.
   */
  const c = status.config;
  useEffect(() => {
    setSource(c.source);
    setEventKey(c.eventKey);
    setIntervalSeconds(String(c.intervalSeconds));
    setImports(c.imports);
    setFields({ ...emptyFieldMap(), ...c.fields });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- siehe oben
  }, [c.source, c.eventKey, c.intervalSeconds]);

  /*
   * RACE RESULT zeigt beim Schluessel der Simple API die ganze Adresse an.
   * Wer sie in eines der beiden Felder kopiert, bekommt sie hier auf beide
   * verteilt -- und sieht dabei, welche Event-ID darin steckte.
   */
  const typeKey = (value: string, field: 'event' | 'key') => {
    const address = parseResultsAddress(value);
    if (address) {
      setEventKey(address.eventKey);
      setApiKey(address.apiKey);
    } else if (field === 'event') setEventKey(value);
    else setApiKey(value);
  };

  const config = (): ResultsConfigInput => ({
    source,
    eventKey: eventKey.trim(),
    apiKey: apiKey.trim(),
    intervalSeconds: Number(interval) || MIN_RESULTS_INTERVAL,
    imports,
    fields,
  });

  const run = async (what: 'save' | 'test') => {
    /*
     * Einschalten -- oder eine andere Liste -- ersetzt den Stand des Events.
     * Das geht nicht zurueck, also vorher fragen. Ein neuer Takt mit derselben
     * Liste fragt nicht; der Server ersetzt dann auch nichts.
     */
    const takeover =
      what === 'save' &&
      source === 'raceresult' &&
      (status.config.source !== 'raceresult' ||
        eventKey.trim() !== status.config.eventKey ||
        // Ein neu eingetippter Schluessel -- derselbe wie bisher (gleiche letzte vier Zeichen) zaehlt nicht.
        (apiKey.trim() !== '' && apiKey.trim().slice(-4) !== status.config.keyHint));
    if (
      takeover &&
      !window.confirm(
        'RACE RESULT wird jetzt die offizielle Liste dieses Events.\n\n' +
          'Mit der ersten Antwort werden ersetzt:\n' +
          '• die Startliste (wer dort nicht steht, wird gestrichen)\n' +
          '• die Teilnehmer-Liste auf der Strecke\n' +
          '• alle bisherigen Meldungen im Ticker — auch eigene Notizen\n\n' +
          'Fortfahren?',
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (what === 'save') {
        await api.saveResults(slug, pin, config());
        // Der Schluessel hat seinen Zweck erfuellt und muss nicht im
        // Formular stehen bleiben, wo ihn der naechste Blick mitliest.
        setApiKey('');
        setProbe(null);
      } else {
        setProbe((await api.testResults(slug, pin, config())).probe);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehlgeschlagen.');
      if (what === 'test') setProbe(null);
    } finally {
      setBusy(false);
    }
  };

  const live = status.phase === 'live' || status.phase === 'stale';

  return (
    <div className="restream">
      <div className="restream__head">
        <span className={`tag tag--rtmp tag--rtmp-${live ? 'live' : status.phase === 'error' ? 'error' : 'idle'}`}>
          {status.phase === 'live' && <span className="dot dot--pulse" />}
          Ergebnisse · {resultsPhaseLabel[status.phase]}
        </span>
        <HelpLink topic="ergebnisse" />
        <span className="stage-bar__sub">
          {live ? <LiveSummary status={status} /> : 'Zieleinläufe von Hand eintragen oder automatisch holen.'}
        </span>
      </div>

      {!enabled && (
        <div className="notice notice--stage">
          Dieser Server fragt keine Ergebnisse ab (RACERESULT=0).
        </div>
      )}
      {status.message && <div className="notice notice--stage">{status.message}</div>}
      {error && <div className="notice notice--stage">{error}</div>}

      <div className="chip-row">
        {RESULTS_SOURCES.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`chip${source === s.id ? ' is-active' : ''}`}
            title={s.hint}
            onClick={() => setSource(s.id)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {source === 'manual' ? (
        <>
          <p className="stage-bar__sub">
            Die Regie trägt Zeiten selbst ein — im Block <b>Live-Ticker</b> unter „Auf der Strecke“.
          </p>
          {status.config.source === 'raceresult' && (
            <button className="btn btn--sm btn--onstage" disabled={busy} onClick={() => void run('save')}>
              Abfrage beenden
            </button>
          )}
        </>
      ) : (
        <>
          <div className="results-grid">
            <label>
              Event-ID
              <input value={eventKey} onChange={(e) => typeKey(e.target.value, 'event')} placeholder="380756" />
            </label>
            <label>
              API-Key
              <input
                type="password"
                value={apiKey}
                onChange={(e) => typeKey(e.target.value, 'key')}
                placeholder={status.config.hasKey ? `gespeichert · ····${status.config.keyHint}` : 'oder ganze Adresse'}
                autoComplete="off"
              />
            </label>
            <label>
              Takt (s)
              <input
                value={interval}
                onChange={(e) => setIntervalSeconds(e.target.value)}
                inputMode="numeric"
                placeholder={`${MIN_RESULTS_INTERVAL}–${MAX_RESULTS_INTERVAL}`}
              />
            </label>
          </div>

          {/* `deck__hint` statt `stage-bar__sub`: Letzteres blendet das Handy aus,
              und ohne diesen Satz findet dort niemand den richtigen Schluessel. */}
          <p className="deck__hint">
            In RACE RESULT unter <b>Simple API</b> einen Schlüssel für die Ergebnisliste anlegen (Format
            JSON) und die ganze Adresse ins Feld „API-Key“ kopieren — die Event-ID steht schon darin.
          </p>

          <div className="chip-row">
            {(
              [
                ['finishes', 'Zieleinläufe'],
                ['splits', 'Zwischenzeiten'],
                ['startlist', 'Startliste'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={`chip${imports[key] ? ' is-active' : ''}`}
                onClick={() => setImports({ ...imports, [key]: !imports[key] })}
              >
                {imports[key] ? '✓ ' : ''}
                {label}
              </button>
            ))}
          </div>

          <div className="row gap-8">
            <button
              className="btn btn--sm btn--onstage"
              disabled={busy || !enabled || !eventKey.trim()}
              onClick={() => void run('test')}
            >
              {busy ? 'fragt ab …' : 'Testabfrage'}
            </button>
            <button
              className="btn btn--sm"
              disabled={busy || !enabled || !eventKey.trim() || (!apiKey.trim() && !status.config.hasKey)}
              onClick={() => void run('save')}
            >
              Abfrage starten
            </button>
          </div>

          {probe && <ProbeResult probe={probe} />}

          <button type="button" className="deck__link" onClick={() => setShowFields((v) => !v)}>
            {showFields ? '▾' : '▸'} Spalten von Hand zuordnen
          </button>
          {showFields && (
            <>
              <p className="stage-bar__sub">
                Leer heißt „automatisch“. Sonst der Spaltenname aus der Testabfrage — oder ihre Nummer ab 1.
              </p>
              <div className="results-grid">
                {RESULTS_FIELDS.map((f) => (
                  <label key={f.id}>
                    {f.label}
                    <input
                      value={fields[f.id]}
                      onChange={(e) => setFields({ ...fields, [f.id]: e.target.value })}
                      placeholder={f.hint}
                    />
                  </label>
                ))}
              </div>
            </>
          )}

          <p className="stage-bar__sub">
            Die Abfrage läuft auf dem Server. Du kannst diese Seite neu laden oder schließen — die
            Zieleinläufe landen weiter im Ticker.
          </p>
        </>
      )}
    </div>
  );
}

/** Was die letzte Runde gebracht hat -- und ob es die letzte war. */
function LiveSummary({ status }: { status: ResultsStatus }) {
  const when = status.lastOkAt ? timeShort.format(new Date(status.lastOkAt)) : null;
  return (
    <>
      {when ? `zuletzt ${when}` : 'wartet auf die erste Antwort'}
      {status.lastRows > 0 ? ` · ${status.lastRows} Zeilen` : ''}
      {status.applied > 0 ? ` · ${status.applied} übernommen` : ''}
      {` · alle ${status.config.intervalSeconds} s`}
    </>
  );
}

/**
 * Die Testabfrage nebeneinander: was wir erkannt haben und was tatsaechlich
 * ankam. Ohne die Rohzeilen waere die Erkennung nicht zu ueberpruefen -- man
 * saehe nur das Ergebnis und nicht, woher es kommt.
 */
function ProbeResult({ probe }: { probe: ResultsProbe }) {
  return (
    <div className="results-probe">
      <b>{probe.message}</b>

      {probe.columns.length > 0 && (
        <p className="results-probe__cols">
          Spalten: {probe.columns.map((c, i) => `${i + 1}. ${c}`).join(' · ')}
        </p>
      )}

      {RESULTS_FIELDS.some((f) => probe.detected[f.id]) && (
        <ul className="results-probe__map">
          {RESULTS_FIELDS.map((f) =>
            probe.detected[f.id] ? (
              <li key={f.id}>
                <span>{f.label}</span>
                <b>{probe.detected[f.id]}</b>
              </li>
            ) : null,
          )}
        </ul>
      )}

      {probe.mapped.length > 0 && (
        <ul className="results-probe__rows">
          {probe.mapped.map((row, i) => (
            <li key={i}>
              <span className="bib">{row.bib || '—'}</span>
              <span>{row.name}</span>
              <span>
                {[
                  row.checkpoint,
                  row.lapSeconds ? `Runde ⌀ ${formatClock(row.lapSeconds)}` : '',
                  row.status === 'onCourse' ? '' : raceStatusLabel[row.status],
                ]
                  .filter(Boolean)
                  .join(' · ') || raceStatusLabel[row.status]}
              </span>
              <span>{row.time}</span>
              <span>{row.place ? `Platz ${row.place}` : ''}</span>
            </li>
          ))}
        </ul>
      )}

      <details>
        <summary>Rohantwort</summary>
        <pre>{JSON.stringify(probe.sample, null, 1)}</pre>
      </details>
    </div>
  );
}
